import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
// Styles
import { Wrapper, Input, Icon, Kbd, Clear } from './Search.styles';

const Search = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const urlTerm = pathname === '/' ? params.get('q') || '' : '';
  const [value, setValue] = useState(urlTerm);
  const inputRef = useRef(null);

  // Keep the field in sync when the URL changes (logo click, back button...)
  useEffect(() => {
    setValue(urlTerm);
  }, [urlTerm]);

  const commit = term => {
    const q = term.trim() ? term : '';
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/', {
      replace: pathname === '/' && Boolean(urlTerm) === Boolean(q)
    });
  };

  // Debounced search while typing
  useEffect(() => {
    if (value === urlTerm) return;
    const timer = setTimeout(() => commit(value), 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, urlTerm]);

  // Press "/" anywhere to jump to search
  useEffect(() => {
    const onKey = event => {
      const tag = document.activeElement && document.activeElement.tagName;
      if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        event.preventDefault();
        inputRef.current && inputRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Wrapper
      role='search'
      onSubmit={event => {
        event.preventDefault();
        commit(value);
      }}
    >
      <Icon viewBox='0 0 24 24' aria-hidden='true'>
        <circle cx='11' cy='11' r='7' />
        <path d='m20 20-3.5-3.5' />
      </Icon>
      <Input
        ref={inputRef}
        type='search'
        placeholder='Search any movie, A to Z…'
        aria-label='Search movies'
        value={value}
        onChange={event => setValue(event.currentTarget.value)}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setValue('');
            event.currentTarget.blur();
          }
        }}
      />
      {value ? (
        <Clear
          type='button'
          aria-label='Clear search'
          onClick={() => {
            setValue('');
            inputRef.current && inputRef.current.focus();
          }}
        >
          ×
        </Clear>
      ) : (
        <Kbd aria-hidden='true'>/</Kbd>
      )}
    </Wrapper>
  );
};

export default Search;
